import React, { useCallback, useEffect, useState } from "react";
import { AcessoHistorico } from "../../../../interfaces/acessos";
import { getHistoricoAcesso } from "../../../../services/acessosApi";
import {
  BTN_SECUNDARIO,
  Carregando,
  Erro,
  ROW,
  TD,
  TH,
  Vazio,
  WRAP_TABELA,
  formatarDataHora,
} from "../../paineis-admin/components/shared";

const POR_PAGINA = 50;

/** Trilha de eventos de acesso das pessoas da organização, 50 por página. */
const HistoricoTab: React.FC<{ orgId: number }> = ({ orgId }) => {
  const [pagina, setPagina] = useState(1);
  const [dados, setDados] = useState<AcessoHistorico | null>(null);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState(false);

  const carregar = useCallback(async () => {
    setLoading(true);
    try {
      setDados(await getHistoricoAcesso(orgId, pagina));
      setErro(false);
    } catch {
      setErro(true);
    } finally {
      setLoading(false);
    }
  }, [orgId, pagina]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  if (loading) return <Carregando rotulo="Carregando histórico" />;
  if (erro || !dados)
    return <Erro texto="Não foi possível carregar o histórico. Tente de novo." onTentar={carregar} />;
  if (dados.itens.length === 0)
    return <Vazio texto="Ainda não há eventos de acesso. Convites, trocas de perfil e desativações aparecerão aqui." />;

  const totalPaginas = Math.max(1, Math.ceil(dados.total / POR_PAGINA));

  return (
    <section aria-label="Histórico de acessos" className="space-y-3">
      <div className={WRAP_TABELA}>
        <table className="w-full min-w-[640px]">
          <thead>
            <tr className="border-b border-gray-100 dark:border-white/10">
              <th className={TH}>Quando</th>
              <th className={TH}>Quem</th>
              <th className={TH}>O quê</th>
              <th className={TH}>Alvo</th>
            </tr>
          </thead>
          <tbody>
            {dados.itens.map((e, i) => (
              <tr key={`${e.quando}-${i}`} className={ROW}>
                <td className={`${TD} whitespace-nowrap`}>{formatarDataHora(e.quando)}</td>
                <td className={TD}>{e.ator}</td>
                <td className={TD}>
                  <span className="font-semibold text-navy-700 dark:text-white">{e.tipo}</span>
                  {e.detalhe && <span className="block text-xs text-gray-500 dark:text-gray-400">{e.detalhe}</span>}
                </td>
                <td className={TD}>{e.alvo}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {totalPaginas > 1 && (
        <nav aria-label="Páginas do histórico" className="flex items-center justify-end gap-3">
          <button type="button" className={BTN_SECUNDARIO} disabled={pagina <= 1} onClick={() => setPagina((p) => p - 1)}>
            Anterior
          </button>
          <span className="text-sm text-gray-600 dark:text-gray-300">
            Página {pagina} de {totalPaginas}
          </span>
          <button
            type="button"
            className={BTN_SECUNDARIO}
            disabled={pagina >= totalPaginas}
            onClick={() => setPagina((p) => p + 1)}
          >
            Próxima
          </button>
        </nav>
      )}
    </section>
  );
};

export default HistoricoTab;
